'use client'

/**
 * Facebook silent-restore glue (ui-react `ClientFacebookService` parity). Loads the FB
 * JS SDK, initializes it, and reads the **silent** login status (`getLoginStatus(cb,
 * true)` — the `true` skips the round-trip-to-Facebook UI). Resolves the access token
 * when the user is already `connected`, else null. Entirely fail-safe — any error or
 * missing SDK resolves null so auto-login never breaks the page.
 *
 * Browser-only; verified via e2e / manual test on a registered origin (FB login status
 * requires the page origin to be a valid App Domain for the app id).
 */

interface FacebookAuthResponse {
    accessToken?: string
}

interface FacebookLoginStatus {
    status?: string
    authResponse?: FacebookAuthResponse
}

interface FacebookSdk {
    init: (config: { appId: string; cookie: boolean; xfbml: boolean; version: string }) => void
    getLoginStatus: (callback: (response: FacebookLoginStatus) => void, roundtrip?: boolean) => void
}

declare global {
    interface Window {
        FB?: FacebookSdk
        fbAsyncInit?: () => void
    }
}

const FB_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js'
const FB_GRAPH_VERSION = 'v19.0'
// Bound the whole silent-restore so a script that loads but never fires `fbAsyncInit`
// (or a `getLoginStatus` callback that never returns) cannot stall the auto-login chain.
const FB_TIMEOUT_MS = 4000

function ensureSdkLoaded(appId: string): Promise<FacebookSdk | null> {
    return new Promise((resolve) => {
        if (window.FB) {
            resolve(window.FB)
            return
        }
        if (document.getElementById('facebook-jssdk')) {
            // Script already injected; wait for fbAsyncInit to finish initializing.
            const prev = window.fbAsyncInit
            window.fbAsyncInit = () => {
                prev?.()
                resolve(window.FB ?? null)
            }
            return
        }

        window.fbAsyncInit = () => {
            try {
                window.FB?.init({ appId, cookie: true, xfbml: false, version: FB_GRAPH_VERSION })
                resolve(window.FB ?? null)
            } catch {
                resolve(null)
            }
        }

        const el = document.createElement('script')
        el.id = 'facebook-jssdk'
        el.src = FB_SDK_URL
        el.async = true
        el.onerror = () => {
            resolve(null)
        }
        document.head.appendChild(el)
    })
}

export async function getFacebookAccessToken(opts: { appId: string }): Promise<string | null> {
    if (typeof window === 'undefined' || !opts.appId) {
        return null
    }
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
        const restore = (async () => {
            const fb = await ensureSdkLoaded(opts.appId)
            if (!fb) {
                return null
            }
            return await new Promise<string | null>((resolve) => {
                fb.getLoginStatus((response) => {
                    const connected = response.status === 'connected'
                    resolve(connected ? (response.authResponse?.accessToken ?? null) : null)
                }, true)
            })
        })()
        const timeout = new Promise<null>((resolve) => {
            timer = setTimeout(() => resolve(null), FB_TIMEOUT_MS)
        })
        return await Promise.race([restore, timeout])
    } catch {
        return null
    } finally {
        if (timer) {
            clearTimeout(timer)
        }
    }
}
